import { useEffect, useState, type ReactNode } from "react";
import { CredentialCategory as CredentialCategorySlug } from "@api/shared-runtime/enums";
import { cn } from "@/lib/utils";
import { useActiveSection } from "../hooks/use-active-section";
import {
  countCredentialsNeedingAttention,
  credentialSectionElementId,
} from "../lib/credential-sections";
import type {
  Credential,
  CredentialCategory,
  CredentialDocument,
  CredentialVerification,
  CredentialsResult,
} from "../types";
import { CompletenessMeter } from "./completeness-meter";
import { CredentialGroup } from "./credential-group";
import { CredentialSectionNav } from "./section-nav";

/** Matches the `accordion-down` keyframe duration in `index.css`. */
const SECTION_EXPAND_ANIMATION_MS = 200;

function scrollToSection(slug: string) {
  document.getElementById(credentialSectionElementId(slug))?.scrollIntoView({
    behavior: "instant",
    block: "start",
  });
}

function groupByCategory(credentials: Credential[]) {
  const byCategory = new Map<string, Credential[]>();
  for (const credential of credentials) {
    const list = byCategory.get(credential.category) ?? [];
    list.push(credential);
    byCategory.set(credential.category, list);
  }
  return byCategory;
}

/**
 * The credential file itself — section rail, completeness meter and grouped
 * cards — with no opinion about who is looking at it or where the data came
 * from.
 *
 * The provider's own page renders this. The organization's equivalent is the
 * dashboard's record pane, which lays the same sections out for a narrow
 * column rather than a full page, so it composes `CredentialGroup` directly
 * instead of coming through here.
 *
 * Chrome that differs between callers (the heading, a provider header) is
 * passed in; anything that would need an `isOrg` branch inside here belongs in
 * the caller instead.
 *
 * See provider-credentials-unified-ui.md §4 principle 3: "same component,
 * different chrome".
 */
export function CredentialsView({
  data,
  userId,
  header,
  readOnly = false,
  onEditCredential,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument,
  onRemoveDocument,
  notice,
  summaryActions,
  uploadArea,
  sectionContent,
  sectionBodies,
  layout = "page",
  initialSection,
  heading = "Your credentials",
}: {
  data: CredentialsResult;
  userId: string;
  /** Rendered above the completeness meter. */
  header?: ReactNode;
  /** Uploads and notices rendered between the meter and the sections. */
  notice?: ReactNode;
  /** Actions shown beside the credential summary. */
  summaryActions?: ReactNode;
  /** Direct upload surface shown above the credential summary. */
  uploadArea?: ReactNode;
  sectionContent?: Partial<Record<string, ReactNode>>;
  /** Replaces a category's standard list while retaining its navigation and counts. */
  sectionBodies?: Partial<Record<string, ReactNode>>;
  /** Supplied when the reader may edit; makes each card's action live. */
  onEditCredential?: (credential: Credential) => void;
  /** Supplied when a document-read endpoint is wired for this reader; without
   * it document rows render without view/download. */
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  isOpeningDocument?: boolean;
  onRemoveDocument?: (input: {
    credential: Credential;
    document: CredentialDocument;
  }) => void;
  /** Hides per-credential edit affordances. Organization surfaces are read-only
   * until the write endpoints exist (O2). */
  readOnly?: boolean;
  layout?: "page" | "panel";
  initialSection?: string;
  heading?: string;
}) {
  const byCategory = groupByCategory(data.credentials);
  // Specialty setup is useful even when this profession has no board
  // certification rows. Keep sections with supplied content in the nav too.
  const sections: {
    category: CredentialCategory;
    credentials: Credential[];
  }[] = data.categories
    .map((category) => ({
      category,
      credentials: byCategory.get(category.slug) ?? [],
    }))
    .filter(
      (section) =>
        section.credentials.length > 0 ||
        sectionContent?.[section.category.slug] ||
        sectionBodies?.[section.category.slug],
    )
    // Start with the provider's basic details. Sorting this new array keeps
    // the query data intact and gives the nav and list the same section order.
    .sort(
      (left, right) =>
        Number(right.category.slug === CredentialCategorySlug.Identity) -
        Number(left.category.slug === CredentialCategorySlug.Identity),
    );

  const activeElementId = useActiveSection(
    layout === "page"
      ? sections.map((section) =>
          credentialSectionElementId(section.category.slug),
        )
      : [],
  );
  /**
   * Sections start closed; only the ones the reader opens are tracked.
   *
   * Every category open at once is a page of a few hundred rows, most of them
   * credentials nobody asked for, and the headings — which say what is in each
   * section and how much of it needs attention — scroll past as fast as the
   * rows do. Closed, the same screen is a contents page: what exists, what is
   * outstanding, and one click into whichever answer is wanted.
   */
  const [expandedSlugs, setExpandedSlugs] = useState<ReadonlySet<string>>(
    () => new Set(initialSection ? [initialSection] : []),
  );

  useEffect(() => {
    if (!initialSection) {
      return;
    }
    // A profile card opens the matching section once the sheet and accordion
    // have mounted. Later scrolling is controlled by the reader and the rail.
    const timeout = setTimeout(
      () => scrollToSection(initialSection),
      SECTION_EXPAND_ANIMATION_MS,
    );
    return () => clearTimeout(timeout);
  }, [initialSection]);

  function setExpanded(slug: string, isExpanded: boolean) {
    setExpandedSlugs((previous) => {
      const next = new Set(previous);
      if (isExpanded) {
        next.add(slug);
      } else {
        next.delete(slug);
      }
      return next;
    });
  }

  // Jumping to a collapsed section would land on a bare header, so expand it
  // first; the section's own scroll margin clears the sticky mobile rail.
  function jumpToSection(slug: string) {
    const wasCollapsed = !expandedSlugs.has(slug);
    setExpanded(slug, true);
    // Expanding animates the page taller, and a section near the bottom needs
    // that extra height before it can reach the top of the viewport — so a
    // collapsed target waits for the animation, while an already-open one
    // only waits the frame it takes for the state change to commit.
    if (wasCollapsed) {
      setTimeout(() => scrollToSection(slug), SECTION_EXPAND_ANIMATION_MS);
      return;
    }
    requestAnimationFrame(() => scrollToSection(slug));
  }

  const navItems = sections.map((section) => ({
    slug: section.category.slug,
    label: section.category.label,
    attentionCount: countCredentialsNeedingAttention(section.credentials),
  }));
  const activeSlug =
    navItems.find(
      (item) => credentialSectionElementId(item.slug) === activeElementId,
    )?.slug ??
    navItems[0]?.slug ??
    null;

  return (
    <div
      className={cn(
        "mx-auto w-full space-y-4",
        layout === "page" && "max-w-6xl p-2 pt-4 md:p-4 md:pt-4",
      )}
    >
      {header}

      {uploadArea}

      <CompletenessMeter
        completeness={data.completeness}
        actions={summaryActions}
        appearance={layout === "panel" ? "panel" : "default"}
      />

      {notice}

      {/* The rail column fits the longest category label at its active
          (semibold) weight, so the highlight never re-wraps a row. */}
      <div
        className={cn(
          "space-y-3",
          layout === "page" &&
            "lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-6 lg:space-y-0",
        )}
      >
        {layout === "page" && (
          <CredentialSectionNav
            items={navItems}
            activeSlug={activeSlug}
            onSelect={jumpToSection}
          />
        )}

        {/* Trailing space so the last sections can still scroll up to the
            reading line — without it a jump to a short final section bottoms
            out early and the nav highlights the section above it. */}
        <div
          className={cn(
            "space-y-4 pb-[30vh]",
            layout === "panel" && "space-y-3",
          )}
        >
          {layout === "panel" && (
            <div className="space-y-1 px-1">
              <h3 className="text-sm font-semibold text-slate-900">
                {heading}
              </h3>
              <p className="text-xs text-slate-600">
                {readOnly
                  ? "Open a category to view its details."
                  : "Open a category to view or edit its details."}
              </p>
            </div>
          )}
          {sections.map((section) => (
            <CredentialGroup
              key={section.category.slug}
              category={section.category}
              credentials={section.credentials}
              userId={userId}
              appearance={layout === "panel" ? "panel" : "default"}
              readOnly={readOnly}
              onEditCredential={onEditCredential}
              onOpenDocument={onOpenDocument}
              onOpenVerificationEvidence={onOpenVerificationEvidence}
              isOpeningDocument={isOpeningDocument}
              onRemoveDocument={onRemoveDocument}
              isExpanded={expandedSlugs.has(section.category.slug)}
              onExpandedChange={(isExpanded) =>
                setExpanded(section.category.slug, isExpanded)
              }
              className={
                layout === "panel" ? "scroll-mt-4 lg:scroll-mt-4" : undefined
              }
              intro={sectionContent?.[section.category.slug]}
            >
              {sectionBodies?.[section.category.slug]}
            </CredentialGroup>
          ))}
        </div>
      </div>
    </div>
  );
}
